(() => {
  'use strict';
  if (typeof HTMLDialogElement === 'undefined' || document.documentElement.hasAttribute('data-product-checkout-initialized')) return;
  document.documentElement.setAttribute('data-product-checkout-initialized', '');
  const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
  const money = cents => new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency || 'USD' }).format(cents / 100);
  const checkoutWindowName = `HomiHavenCheckout-${Date.now()}`;
  const submittedSelections = new Set();
  let dialog, trigger, checkoutWindow;

  function selectionKey(form) {
    return JSON.stringify([form.getAttribute('id'), [...new FormData(form)].map(([name, value]) => [name, value instanceof File ? (value.name === '' && value.size === 0 ? null : [value.name, value.size, value.lastModified]) : value])]);
  }

  function checkoutStatus(message) {
    const status = dialog?.querySelector('[data-checkout-status]');
    if (status) status.textContent = message;
  }

  function syncCheckoutButton(form) {
    const button = dialog.querySelector('[data-checkout-submit]');
    if (!button) return;
    const submitted = submittedSelections.has(selectionKey(form));
    button.setAttribute('form', form.getAttribute('id'));
    button.setAttribute('formtarget', checkoutWindowName);
    button.querySelector('[data-checkout-submit-label]').textContent = submitted ? button.dataset.checkoutResumeLabel : button.dataset.checkoutLabel;
    const fallback = dialog.querySelector('[data-checkout-current-tab]');
    fallback.setAttribute('form', form.getAttribute('id'));
    fallback.hidden = true;
    checkoutStatus(submitted ? 'Your item has been sent to checkout. Return to the checkout window to continue.' : '');
  }

  function checkout(event, form) {
    const button = event.submitter;
    const key = selectionKey(form);
    const submitted = submittedSelections.has(key);
    if (form.dataset.submitting === 'true') {
      event.preventDefault();
      checkoutStatus('Please wait for your cart to finish updating, then try again.');
      return;
    }
    if (button.matches('[data-checkout-current-tab]')) {
      if (submitted) { event.preventDefault(); location.assign(button.value); }
      return;
    }
    if (submitted) event.preventDefault();
    if (!checkoutWindow || checkoutWindow.closed) {
      checkoutWindow = window.open(submitted ? button.value : '', checkoutWindowName, 'popup,width=1100,height=850,scrollbars=yes,resizable=yes');
    }
    if (!checkoutWindow || checkoutWindow.closed) {
      event.preventDefault();
      checkoutStatus('Your browser blocked the checkout window. Allow pop-ups and try again, or continue checkout in this tab.');
      dialog.querySelector('[data-checkout-current-tab]').hidden = false;
      return;
    }
    checkoutWindow.focus();
    submittedSelections.add(key);
    syncCheckoutButton(form);
  }

  function close() { if (dialog?.open) dialog.close(); }

  function closed() {
    document.body.classList.remove('product-checkout-open');
    document.querySelectorAll('[data-buy-now]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    if (trigger?.isConnected && !trigger.disabled) trigger.focus({ preventScroll: true });
  }

  function open(form, source) {
    if (!dialog || form.dataset.submitting === 'true' || !form.reportValidity()) return;
    const section = form.closest('[data-product-section]');
    const product = JSON.parse(section.querySelector('[data-product-json]')?.textContent || '{}');
    const formData = new FormData(form);
    const variant = product.variants?.find(item => String(item.id) === formData.get('id'));
    const quantity = Number(formData.get('quantity') || 1);
    const sellingPlanId = formData.get('selling_plan') || null;
    const allocation = variant?.selling_plan_allocations?.find(item => String(item.selling_plan.id) === sellingPlanId);
    if (!variant?.available || (variant.requires_selling_plan && !allocation) || !Number.isInteger(quantity) || quantity < 1) return;
    const content = {
      product: product.title,
      variant: variant.title === 'Default Title' ? '' : variant.title,
      plan: allocation?.selling_plan.name || '',
      quantity,
      subtotal: money((allocation ? allocation.price : variant.price) * quantity)
    };
    Object.entries(content).forEach(([key, value]) => {
      const element = dialog.querySelector(`[data-checkout-${key}]`);
      if (element) { element.textContent = value; element.hidden = value === ''; }
    });
    const mediaId = variant.featured_media?.id;
    const selectedImage = (mediaId && section.querySelector(`[data-product-media-id="${CSS.escape(String(mediaId))}"] img`)) || section.querySelector('[data-product-gallery] img');
    const image = dialog.querySelector('[data-checkout-image]');
    if (image) {
      image.hidden = !selectedImage;
      if (selectedImage) { image.src = selectedImage.currentSrc || selectedImage.src; image.alt = selectedImage.alt || product.title; }
    }
    trigger = source;
    syncCheckoutButton(form);
    dialog.dataset.productId = String(product.id);
    dialog.dataset.variantId = String(variant.id);
    dialog.dataset.quantity = String(quantity);
    dialog.dataset.sellingPlanId = sellingPlanId || '';
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('product-checkout-open');
    source?.setAttribute('aria-expanded', 'true');
    dialog.querySelector('[data-checkout-close]').focus({ preventScroll: true });
    dialog.dispatchEvent(new CustomEvent('product:checkout:open', {
      bubbles: true,
      detail: { productId: product.id, variantId: variant.id, quantity, sellingPlanId, form }
    }));
  }

  function init() {
    const current = document.querySelector('[data-product-checkout]');
    if (dialog !== current) {
      close();
      dialog = current;
      if (dialog) {
        dialog.addEventListener('close', closed);
        dialog.addEventListener('click', event => {
          if (event.target.closest('[data-checkout-close]')) { close(); return; }
          if (event.target !== dialog) return;
          const bounds = dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
        });
      }
    }
    document.querySelectorAll('[data-buy-now]').forEach(button => {
      if (dialog) {
        button.setAttribute('aria-controls', dialog.id);
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-expanded', String(dialog.open));
      } else {
        ['aria-controls', 'aria-haspopup', 'aria-expanded'].forEach(attribute => button.removeAttribute(attribute));
      }
    });
  }

  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !dialog) return;
    if (event.submitter?.matches('[data-checkout-submit], [data-checkout-current-tab]')) {
      if (event.defaultPrevented) return;
      checkout(event, form);
      return;
    }
    if (!event.submitter?.matches('[data-buy-now]')) return;
    event.preventDefault();
    open(form, event.submitter);
  });
  document.addEventListener('keydown', event => {
    if (!dialog?.open || event.key !== 'Tab') return;
    const controls = [...dialog.querySelectorAll('a[href], button, input, select, textarea, iframe, [tabindex]')]
      .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && !element.closest('[hidden]') && element.getClientRects().length);
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
      event.preventDefault(); first?.focus();
    }
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.contains(dialog)) { close(); dialog = null; closed(); }
  });
  document.addEventListener('shopify:section:select', event => {
    if (!dialog || !event.target.contains(dialog)) return;
    const form = document.querySelector('[data-product-form]');
    if (form) open(form, form.querySelector('[data-buy-now]'));
  });
  document.addEventListener('shopify:section:deselect', event => { if (event.target.contains(dialog)) close(); });
  init();
})();
